<!--
  AI 草稿（待审核）卡片 —— 内联在「消息管理」工作台的会话流里（REQ-2026-574277 二期修订 §4）。

  原来是独立页面 /aiCs/review，现在并进工作台：客服在哪儿聊天，就在哪儿审核。

  ⚠️ 三个操作（发送 / 编辑后发送 / 忽略）**一律走 aiReviewApi → admin-api → app-server 内部接口**，
     **不得改用本页的腾讯 IM Web SDK 直发**。
     工作台的手工消息是浏览器直发的，审核发送如果跟着走那条，
     署名 / 落库 / 限流 三处都会与服务端那条分叉 —— 而且两边都不报错。
-->
<template>
  <div v-if="drafts.length" class="ai-draft-wrap">
    <div v-for="draft in drafts" :key="draft.id" class="ai-draft-card">
      <div class="ai-draft-head">
        <span class="ai-draft-title">AI 草稿（待审核）</span>
        <!--
          「按人」必须在界面上可见：分给谁、转过几次，都显示出来。
          §3.4 转接的验证判据是"归属人变了"，不是"列表多了少了"。
        -->
        <span v-if="draft.assigneeName" class="ai-draft-meta">归属：{{ draft.assigneeName }}</span>
        <span v-if="draft.transferCount > 0" class="ai-draft-meta warn">已转接 {{ draft.transferCount }} 次</span>
        <span class="ai-draft-meta">{{ sourceText(draft.answerSource) }}</span>
      </div>

      <div class="ai-draft-question">
        <span class="label">客户问：</span>{{ draft.questionText || (isImage(draft) ? '（客户发来一张图片）' : '') }}
        <!--
          图片问题：把图直接画在卡片里。客服要回复的就是这张图，不能让他再去对话流里翻。
          用 el-image 的预览而不是裸 img：原图可能很大，卡片里只放缩略尺寸，点开看大图。
        -->
        <div v-if="isImage(draft) && draft.imageUrl" class="ai-draft-image">
          <el-image :src="draft.imageUrl" :preview-src-list="[draft.imageUrl]" fit="cover" />
        </div>
      </div>

      <div v-if="editingId !== draft.id" class="ai-draft-answer">
        <span class="label">草稿：</span>
        <span v-if="draft.draftAnswer">{{ draft.draftAnswer }}</span>
        <span v-else class="empty">（AI 未给出答案，请人工回复）</span>
      </div>
      <el-input
        v-else
        v-model="editText"
        type="textarea"
        :rows="4"
        maxlength="1000"
        show-word-limit
        :placeholder="draftOf(editingId) && draftOf(editingId).draftAnswer ? '编辑后发送给客户' : '输入给客户的回复'"
      />

      <div class="ai-draft-actions">
        <template v-if="editingId !== draft.id">
          <!-- 按钮在请求期间置灰：幂等由服务端保证，但要让客服**看得到**"点过了" -->
          <!--
            没有草稿时只给「人工回复 / 忽略」：没有草稿就谈不上"发送"和"编辑后发送"。
            留着一个永远置灰的「发送」按钮，客服会以为是自己哪里没操作对。
          -->
          <template v-if="draft.draftAnswer">
            <el-button size="mini" type="primary" :disabled="sending" @click="handleSend(draft)">发送</el-button>
            <el-button size="mini" :disabled="sending" @click="handleEdit(draft)">编辑后发送</el-button>
          </template>
          <el-button v-else size="mini" type="primary" :disabled="sending" @click="handleEdit(draft)">人工回复</el-button>
          <el-button
            size="mini"
            type="text"
            class="danger-text"
            :disabled="sending"
            @click="handleIgnore(draft)"
          >忽略</el-button>
        </template>
        <template v-else>
          <el-button
            size="mini"
            type="primary"
            :disabled="!editText.trim() || sending"
            @click="handleEditSend(draft)"
          >发送</el-button>
          <el-button size="mini" :disabled="sending" @click="cancelEdit">取消</el-button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import aiReviewApi from '@/api/im/aiReview'

/** cust_service_ai_review.status：1 待审核 */
const STATUS_PENDING = 1

export default {
  name: 'AiDraftCard',
  props: {
    /**
     * 当前会话的 im_conversation.id。
     *
     * ⚠️ 工作台手里的是**客服侧镜像行**的 id，而审核队列存的是**客户侧行**的 id，
     *    两者不是同一个数。这里**原样传给后端即可** —— 后端会把它展开成会话对再查
     *    （AiCsReviewService_TEE.conversationPairIds）。前端不要自己去换算。
     */
    conversationId: { type: [Number, String], default: null }
  },
  data() {
    return {
      drafts: [],
      sending: false,
      editingId: null,
      editText: ''
    }
  },
  watch: {
    conversationId: {
      immediate: true,
      handler() {
        this.cancelEdit()
        this.load()
      }
    }
  },
  methods: {
    /** 供父组件在收到新消息后调用：客户又发了一条，草稿可能变了 */
    reload() {
      this.load()
    },
    load() {
      if (!this.conversationId) {
        this.drafts = []
        return
      }
      const asked = this.conversationId
      aiReviewApi.fetchList({
        status: STATUS_PENDING,
        imConversationId: this.conversationId,
        pageNo: 1,
        pageSize: 5
      }).then(({ data }) => {
        // ⚠️ 防串台：请求发出后客服可能已经切到别的会话了，
        //    晚到的响应不能把上一个会话的草稿画到当前会话里。
        if (asked !== this.conversationId) return
        this.drafts = (data && data.list) || []
      }).catch(() => {
        // 审核接口挂了不能连累聊天本身：卡片不显示就是了
        if (asked === this.conversationId) this.drafts = []
      })
    },
    sourceText(source) {
      return { RAG: '来源：知识库', QWEN: '来源：大模型', NONE: '来源：无' }[source] || ''
    },
    handleEdit(draft) {
      this.editingId = draft.id
      this.editText = draft.draftAnswer || ''
    },
    cancelEdit() {
      this.editingId = null
      this.editText = ''
    },
    handleSend(draft) {
      this.doSend(draft.id, 'SEND', null)
    },
    handleEditSend(draft) {
      // 有草稿 = 改了 AI 的答案再发（EDIT_SEND，服务端会把它记成 final_answer）；
      // 没草稿 = 纯人工回复（MANUAL_REPLY，服务端不覆盖 draft/final —— 那两列是"草稿"的语义，
      // 硬塞进去的话「AI 回复记录」里会出现一条"AI 草稿为空、最终答案却有内容"的怪记录，统计也会把它算成"审核后发出"）。
      const action = draft.draftAnswer ? 'EDIT_SEND' : 'MANUAL_REPLY'
      this.doSend(draft.id, action, this.editText.trim())
    },
    /** cust_service_ai_review.question_type：2 = 图片 */
    isImage(draft) {
      return Number(draft.questionType) === 2
    },
    draftOf(id) {
      return this.drafts.find(d => d.id === id)
    },
    doSend(id, action, text) {
      if (this.sending) return
      this.sending = true
      aiReviewApi.send({ id, action, text })
        .then(() => {
          this.$message.success('已发送')
          this.cancelEdit()
          this.afterChanged()
        })
        .catch(() => {
          // 错误提示由 request 拦截器统一弹（服务端会给出原因，如"该草稿已被处理，或已被客户的新消息合并"）。
          // 这里要做的是**把卡片刷掉**：发送被拒多半是因为这条草稿已经不是待审核了，
          // 卡片还留在那儿的话客服只会接着再点一次。不调 afterChanged —— 消息流没变，不用整屏重拉。
          this.cancelEdit()
          this.load()
          this.$EventBus.$emit('aiReviewChanged')
        })
        .finally(() => { this.sending = false })
    },
    handleIgnore(draft) {
      this.$confirm('忽略后这条草稿不会发给客户，确定吗？', '提示', { type: 'warning' })
        .then(() => {
          this.sending = true
          return aiReviewApi.ignore(draft.id)
        })
        .then(() => {
          this.$message.success('已忽略')
          this.afterChanged()
        })
        .catch(() => {})
        .finally(() => { this.sending = false })
    },
    afterChanged() {
      this.load()
      // 通知父组件：消息流要刷新（发出去的那条要出现）、顶部待审计数要变
      this.$emit('changed')
    }
  }
}
</script>

<style scoped>
.ai-draft-wrap { padding: 0 16px 8px; }
.ai-draft-card {
  border: 1px dashed #e6a23c;
  background: #fdf6ec;
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 8px;
  font-size: 13px;
}
.ai-draft-head { margin-bottom: 6px; }
.ai-draft-title { font-weight: 600; color: #e6a23c; margin-right: 10px; }
.ai-draft-meta { color: #909399; margin-right: 10px; font-size: 12px; }
.ai-draft-meta.warn { color: #f56c6c; }
.ai-draft-question, .ai-draft-answer { line-height: 1.6; word-break: break-all; white-space: pre-wrap; }
/*
  长草稿（RAG 多条目、QWEN 兜底几百字）要滚在**正文里**，不能让整张卡片去滚 ——
  卡片一滚，底下的「发送 / 编辑后发送 / 忽略」就跟着滚出视野，客服得先滚一段才能点，
  而他多半根本不知道下面还有按钮。正文封顶、按钮常驻，才是对的。
*/
.ai-draft-question { max-height: 90px; overflow-y: auto; }
.ai-draft-answer { max-height: 150px; overflow-y: auto; }
/* 这个 style 块是纯 CSS（没有 lang="scss"），不能写嵌套 —— 嵌套规则会被浏览器整条丢掉且不报错 */
.ai-draft-image { margin-top: 6px; white-space: normal; }
.ai-draft-image .el-image { width: 120px; height: 120px; border-radius: 4px; cursor: pointer; }
.ai-draft-question { color: #606266; margin-bottom: 4px; }
.label { color: #909399; }
.empty { color: #c0c4cc; }
.ai-draft-actions { margin-top: 8px; text-align: right; }
.danger-text { color: #f56c6c; }
</style>
